/* eslint-disable  @typescript-eslint/no-unused-vars */

import classes from "../ThousandPropertiesNewRails.module.scss";
import CompactInventoryCard from "@Components/CompactInventoryCard";
import DefaultCompactInventoryCard from "@Pages/Home/components/HomepageCollectionSkeleton/DefaultCompactInventoryCard";
import HorizontalScroll from "@Components/HorizontalScroll";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import { APP_PAGE } from "@Utils/webApputils/analyticEvents";

interface SSRContentRendererProps {
  title: string;
  gtagEventAction: string;
  gtagEventCategory: string;
  isDesktop: boolean;
  isRequestActive: boolean;
  getSSRDisplayCollection: () => any[];
  getHiddenSSRData: () => any[];
}

// Hidden SEO Cards Component
const HiddenSEOCards: React.FC<{
  title: string;
  groupedData: any;
  isDesktop: boolean;
}> = ({ title, groupedData, isDesktop }) => (
  <div
    style={{ position: "absolute", left: "-9999px", visibility: "hidden" }}
    aria-hidden="true"
    role="complementary"
    aria-label={`${title} - Additional property listings by location`}
  >
    {Object.entries(groupedData || {})
      .map(([country, cities]) => ({ country, cities }))
      .map((countryData: any, countryIndex: number) => {
        const countryName = countryData.country || `Country-${countryIndex}`;
        const cities = countryData.cities || [];

        return (
          <section key={countryName} aria-labelledby={`country-${countryName}`}>
            <h3 id={`country-${countryName}`} itemProp="name">
              Properties in {countryName}
            </h3>
            <div itemScope itemType="https://schema.org/Country">
              <meta itemProp="name" content={countryName} />
              {Array.isArray(cities) &&
                cities.map((cityData: any, cityIndex: number) => {
                  const cityName = cityData.city || `City-${cityIndex}`;
                  const inventories = cityData.inventories || [];

                  return (
                    <article key={cityName} aria-labelledby={`city-${cityName}`}>
                      <h4 id={`city-${cityName}`} itemProp="name">
                        {cityName} Properties
                      </h4>
                      <div itemScope itemType="https://schema.org/City">
                        <meta itemProp="name" content={cityName} />
                        <meta itemProp="containedInPlace" content={countryName} />
                        {Array.isArray(inventories) && inventories.length > 0 && (
                          <div role="list" aria-label={`Properties in ${cityName}`}>
                            {inventories.slice(0, 2).map((inventory: any, index: number) => (
                              <div
                                key={`hidden-${countryName}-${cityName}-${inventory?.id || index}`}
                                role="listitem"
                              >
                                <CompactInventoryCard
                                  dataTestId={`${title}-hidden-${countryName}-${cityName}`}
                                  item={inventory}
                                  key={`hidden-${countryName}-${cityName}-${
                                    inventory?.id || index
                                  }`}
                                  isRequestActive={false}
                                  showIcons={false}
                                  gtagEventAction=""
                                  gtagEventCategory=""
                                  isDesktop={isDesktop}
                                  screenName={APP_PAGE.HOME}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
            </div>
          </section>
        );
      })}
  </div>
);

// Main Content Component
const MainContent: React.FC<{
  title: string;
  displayCollectionLocal: any[];
  isDesktop: boolean;
  isRequestActive: boolean;
  gtagEventAction: string;
  gtagEventCategory: string;
}> = ({
  title,
  displayCollectionLocal,
  isDesktop,
  isRequestActive,
  gtagEventAction,
  gtagEventCategory,
}) => (
  <HorizontalScroll
    dataTestId={title}
    showControls={isDesktop}
    className={formatClassNames(classes.horizontalScrollCr, isDesktop && classes.desktopCr)}
    bgColor="white"
  >
    {Array.isArray(displayCollectionLocal) &&
      displayCollectionLocal.map((item, i) => (
        <CompactInventoryCard
          dataTestId={title}
          item={item}
          key={`${item?.id || "asd"}-${i}`}
          isRequestActive={isRequestActive}
          showIcons
          gtagEventAction={gtagEventAction}
          gtagEventCategory={gtagEventCategory}
          isDesktop={isDesktop}
          screenName={APP_PAGE.HOME}
        />
      ))}
    {Array.from({ length: 8 }).map((_, index) => (
      <DefaultCompactInventoryCard key={index} />
    ))}
  </HorizontalScroll>
);

const SSRContentRenderer: React.FC<SSRContentRendererProps> = ({
  title,
  gtagEventAction,
  gtagEventCategory,
  isDesktop,
  isRequestActive,
  getSSRDisplayCollection,
  getHiddenSSRData,
}) => {
  const displayCollectionLocal = getSSRDisplayCollection();
  const hiddenSSRData = getHiddenSSRData();

  return (
    <>
      <MainContent
        title={title}
        displayCollectionLocal={displayCollectionLocal}
        isDesktop={isDesktop}
        isRequestActive={isRequestActive}
        gtagEventAction={gtagEventAction}
        gtagEventCategory={gtagEventCategory}
      />
      <HiddenSEOCards title={title} groupedData={hiddenSSRData} isDesktop={isDesktop} />
    </>
  );
};

export default wrapperHOC(SSRContentRenderer, {
  componentName: "SSRContentRenderer-thousandproperty",
  showForChina: true,
});
